/**
 * API utility functions for the Golf Course API
 */

export type User = {
  id: number;
  created_at: string;
  email: string;
  activated: boolean;
  subscription: string;
  price_cents?: number | null;
}

export type Location = {
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
};

export type Hole = {
  par?: number;
  yardage?: number;
  meters?: number;
  handicap?: number;
};

export type TeeBox = {
  tee_name?: string;
  course_rating?: number;
  slope_rating?: number;
  bogey_rating?: number;
  total_yards?: number;
  total_meters?: number;
  number_of_holes?: number;
  par_total?: number;
  front_course_rating?: number;
  front_slope_rating?: number;
  front_bogey_rating?: number;
  back_course_rating?: number;
  back_slope_rating?: number;
  back_bogey_rating?: number;
  holes: Hole[];
};

export type TeeBoxGrouping = {
  male?: TeeBox[];
  female?: TeeBox[];
};

// The summary form of TeeBoxGrouping: how many tee boxes each grouping holds.
export type TeeBoxCounts = {
  male?: number;
  female?: number;
};

export type GolfCourse = {
  id: number;
  club_name?: string;
  course_name: string;
  location: Location;
  tees: TeeBoxGrouping;
  scorecard_url?: string;
};

// What /v1/frontend/search returns. Same identifying and location fields as
// GolfCourse, but "tees" is a count per grouping rather than the tee boxes
// themselves — search deliberately omits the tee box detail so a result list
// isn't 25 full scorecards. Anything needing tee data fetches the course by
// id (see fetchCourse), which is what the editor does on select.
export type CourseSearchResult = {
  id: number;
  club_name?: string;
  course_name: string;
  location: Location;
  tees: TeeBoxCounts;
  scorecard_url?: string;
};

export type UsageDay = {
  day: string;
  count: number;
  // Requests refused because the day's limit had already been reached.
  // Disjoint from count, which is requests served and never exceeds the limit,
  // so count + overage is the day's total attempted demand. Optional so a
  // dashboard deployed ahead of the API that added it reads 0, not NaN.
  overage?: number;
};

export type Usage = {
  // 7 entries oldest-first; the last entry is today.
  history: UsageDay[];
  limit: number;
  // Today's refused count, mirroring today.overage.
  overage?: number;
  subscription: string;
  today: UsageDay;
};

// Metadata about the user's API key. The plaintext is never available here:
// the backend stores only a SHA-256 hash, so the key itself is only ever
// surfaced once, in the rotation response.
export type ApiKeyInfo = {
  exists: boolean;
  // Null for a key that has never been rotated, i.e. the one issued at
  // registration.
  last_rotated_at: string | null;
};

export type CourseUpdatePayload = {
  club_name?: string;
  course_name?: string;
  location?: Location;
  tees?: TeeBoxGrouping;
  scorecard_url?: string;
};

// The course endpoints branch on status codes (401 dead session, 403 wrong
// tier, 409 edit conflict, 422 validation), so their wrappers return the
// status alongside the parsed body instead of unwrapping like the wrappers
// below.
//
// `error` stays `any` because the API genuinely returns more than one shape:
// a plain string for most failures, but a field -> message object from
// failedValidationResponse. The discipline that makes that safe is to never
// render `error` into JSX directly — always put it through errorMessage below,
// or a validation body throws "Objects are not valid as a React child".
export type ApiResult<T> = {
  status: number;
  data?: T;
  error?: any;
};

/** The tiers that have a Stripe subscription behind them. */
export type PaidSubscription = "pro" | "enterprise";

/**
 * Flattens whatever the API put in `error` into one line of display copy.
 * Handles the string form (Stripe's card message, the tier-guard messages —
 * both already written for a human), the field -> message object from
 * failedValidationResponse, and an array of either. Anything unrecognized or
 * empty falls back, so a caller can always render the result.
 */
export function errorMessage(error: unknown, fallback: string): string {
  const parts: string[] = [];

  const collect = (value: unknown) => {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) parts.push(trimmed);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(collect);
      return;
    }
    if (value && typeof value === "object") {
      Object.values(value as Record<string, unknown>).forEach(collect);
    }
  };

  collect(error);
  if (parts.length === 0) {
    return fallback;
  }

  // Give the joined sentences terminal punctuation; the API's messages don't
  // carry any, so "a. b" reads better than "a b".
  return parts.map((part) => (/[.!?]$/.test(part) ? part : `${part}.`)).join(" ");
}

/**
 * Parses a JSON body without letting a malformed one escape the wrapper.
 * The other wrappers here assume `response.json()` resolves; an empty body or
 * an HTML error page from a proxy makes it throw, which skips the wrapper's
 * own error branch and rejects the promise instead. New wrappers use this.
 */
async function readJson(response: Response): Promise<any> {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

// The only user allowed to see the course-update screen. This gate is UX
// only — the API still enforces a pro/enterprise session on every request.
export const ADMIN_EMAIL = "regan.c.loper@gmail.com";

// Base URL of the API. Overridable so local dev can point at a local backend
// (session cookies won't cross from localhost to the prod API host).
const API_BASE_URL =
  process.env.GATSBY_API_BASE_URL ?? "https://api.golfcourseapi.com";

// Stripe checkout is a plain HTML form POST (email travels in the body, no
// session cookie involved), so pages need the raw URL for the form action.
export const CHECKOUT_URL = `${API_BASE_URL}/create-checkout-session`;

// All /v1/frontend/* endpoints are gated by an Origin allowlist and
// authenticate the user via the httpOnly gcapi_session cookie — hence
// credentials: "include" on every call. The cookie is SameSite=Lax and
// golfcourseapi.com → api.golfcourseapi.com is same-site, so it rides along
// without the custom header.
//
// Reads deliberately send nothing but CORS-safelisted headers. X-Frontend-Key
// is not safelisted, so every request carrying it costs an extra OPTIONS
// round trip; on a read there is nothing for it to protect that the session
// cookie and the Origin allowlist don't already cover. Writes keep sending
// it: the custom header is what forces a preflight, and that preflight —
// which a cross-site form post cannot make — is a layer of the CSRF defense,
// alongside the Origin check and SameSite=Lax. The key is inlined into the
// client bundle at build (it isn't a true secret) and must match the
// backend's FRONTEND_SECRET.
async function frontendFetch(
  path: string,
  init: RequestInit = {}
): Promise<Response> {
  const isRead = (init.method ?? "GET") === "GET";

  return fetch(`${API_BASE_URL}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      // Accept is safelisted at this value; anything else here (a custom
      // header, an arbitrary Content-Type) reinstates the preflight.
      ...(isRead
        ? { Accept: "application/json" }
        : { "X-Frontend-Key": process.env.GATSBY_FRONTEND_SECRET ?? "" }),
      ...init.headers,
    },
  });
}

// Options accepted by the read-only session endpoints below.
export type FetchOptions = {
  // Skip de-duplication and always hit the network. For refreshes that follow
  // an action the user just took, where a cached answer would be stale.
  force?: boolean;
};

// How long a settled result stays reusable. Long enough to absorb a second
// mount of the same tree, short enough that no view shows stale data.
const DEDUPE_WINDOW_MS = 2000;

const inFlight = new Map<string, Promise<any>>();

/**
 * Collapses duplicate GETs to the same endpoint. The dashboard mounts its
 * providers twice on load (hydration re-render), which otherwise doubles every
 * session request and — against the API's per-IP burst of 4 — pushes the last
 * one into a 429. Concurrent callers share the in-flight promise, and the
 * result stays reusable for a beat after it settles so a remount moments later
 * costs nothing. Failures are dropped immediately, so a retry really retries.
 */
function deduped<T>(
  key: string,
  fn: () => Promise<T>,
  { force = false }: FetchOptions = {}
): Promise<T> {
  if (!force) {
    const pending = inFlight.get(key) as Promise<T> | undefined;
    if (pending) return pending;
  }

  const request = fn();
  inFlight.set(key, request);

  const drop = () => {
    if (inFlight.get(key) === request) inFlight.delete(key);
  };
  request.then(() => setTimeout(drop, DEDUPE_WINDOW_MS), drop);

  return request;
}

export function getDisplayPriceUsd(user: User | undefined, fallbackUsd: string): string {
  if (user?.price_cents != null) {
    return (user.price_cents / 100).toFixed(2);
  }
  return fallbackUsd;
}

/**
 * Fetches the signed-in user from the session cookie.
 * @param options Pass force to bypass request de-duplication
 * @returns The user, or null when there is no valid session (401)
 */
export async function getCurrentUser(
  options?: FetchOptions
): Promise<User | null> {
  return deduped(
    "me",
    async () => {
      const response = await frontendFetch("/v1/frontend/me");
      if (!response.ok) {
        return null;
      }
      const formatted = await response.json();
      return formatted.user ?? null;
    },
    options
  );
}

/**
 * Asks the backend to email a sign-in magic link. Always succeeds (202)
 * whether or not the email has an account, to avoid enumeration.
 * @param email The user's email address
 * @returns Response with success or error
 */
export async function requestLogin(email: string) {
  const response = await frontendFetch("/v1/frontend/login-request", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
  return await response.json();
}

/**
 * Exchanges a sign-in magic-link token for a session. On 201 the backend
 * sets the httpOnly session cookie; the token is single-use.
 * @param token The sign-in token from the magic-link
 * @returns The signed-in user with response status (422 = invalid/expired)
 */
export async function createSession(token: string): Promise<ApiResult<User>> {
  const response = await frontendFetch("/v1/frontend/sessions", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
  const formatted = await response.json();
  if (!response.ok) {
    return { status: response.status, error: formatted?.error };
  }
  return { status: response.status, data: formatted.user };
}

/**
 * Logs out: deletes the current session server-side and clears the cookie.
 * Errors are swallowed — the caller always clears its local auth state.
 */
export async function deleteSession(): Promise<void> {
  try {
    await frontendFetch("/v1/frontend/sessions", { method: "DELETE" });
  } catch {
    // Logout must never fail from the user's perspective.
  }
}

/**
 * Rotates the signed-in user's API key in one step. The new key is returned
 * once in the response (the backend also emails it).
 * @returns The new API key with response status
 */
export async function rotateApiKey(): Promise<ApiResult<{ api_key: string }>> {
  const response = await frontendFetch("/v1/frontend/api-key/rotate", {
    method: "POST",
  });
  const formatted = await response.json();
  if (!response.ok) {
    return { status: response.status, error: formatted?.error };
  }
  return { status: response.status, data: formatted };
}

/**
 * Fetches the signed-in user's API usage: a 7-day request history plus
 * today's count and the plan's daily limit.
 * @param options Pass force to bypass request de-duplication
 * @returns The usage stats with response status
 */
export async function getUsage(
  options?: FetchOptions
): Promise<ApiResult<Usage>> {
  return deduped(
    "usage",
    async () => {
      const response = await frontendFetch("/v1/frontend/usage");
      const formatted = await response.json();
      if (!response.ok) {
        return { status: response.status, error: formatted?.error };
      }
      return { status: response.status, data: formatted.usage };
    },
    options
  );
}

/**
 * Fetches metadata about the signed-in user's API key (whether one exists
 * and when it was last rotated). The plaintext key cannot be fetched — see
 * ApiKeyInfo. Called lazily when the user asks to reveal key details.
 * @param options Pass force to bypass request de-duplication
 * @returns The key metadata with response status
 */
export async function getApiKeyInfo(
  options?: FetchOptions
): Promise<ApiResult<ApiKeyInfo>> {
  return deduped(
    "api-key",
    async () => {
      const response = await frontendFetch("/v1/frontend/api-key");
      const formatted = await response.json();
      if (!response.ok) {
        return { status: response.status, error: formatted?.error };
      }
      return { status: response.status, data: formatted.api_key };
    },
    options
  );
}

/**
 * Cancels the signed-in user's subscription (identity from the session). The
 * cancellation is immediate — the account is on the free limit as soon as this
 * resolves.
 *
 * The backend writes the account back to free before it responds, so the user
 * it returns is authoritative and callers should apply it rather than waiting
 * on a follow-up /me.
 * @returns The updated (now free) user with response status
 */
export async function cancelSubscription(): Promise<ApiResult<User>> {
  const response = await frontendFetch("/v1/frontend/subscription", {
    method: "DELETE",
  });
  const formatted = await readJson(response);
  if (!response.ok) {
    return { status: response.status, error: formatted?.error };
  }
  return { status: response.status, data: formatted.user };
}

/**
 * Moves a paying subscriber between the paid tiers, repricing the existing
 * Stripe subscription rather than starting a second one. Free accounts are not
 * handled here and get a 422 — they have nothing to reprice and belong in
 * checkout (see CHECKOUT_URL).
 *
 * Both the route and the body come from the one `target` argument, which is
 * why this is a single function: the backend rejects a body whose tier
 * disagrees with the route, and deriving both from the same value makes that
 * mismatch impossible to write. The body is optional server-side; sending it
 * anyway keeps the call legible in the network tab.
 *
 * Like cancelSubscription, the tier is written locally before the response, so
 * the returned user is authoritative.
 *
 * Statuses worth branching on: 402 the card was declined and Stripe rolled the
 * change back (nothing was charged, the tier is unchanged); 422 the account
 * isn't on a tier this move is valid from, or its subscription has money
 * outstanding — retrying cannot help.
 * @param target The tier to move to
 * @returns The updated user with response status
 */
export async function changeSubscriptionTier(
  target: PaidSubscription
): Promise<ApiResult<User>> {
  // "enterprise" is the only tier above "pro", so the direction is implied by
  // the target alone.
  const direction = target === "enterprise" ? "upgrade" : "downgrade";

  const response = await frontendFetch(
    `/v1/frontend/subscription/${direction}`,
    {
      method: "POST",
      body: JSON.stringify({ tier: target }),
    }
  );
  const formatted = await readJson(response);
  if (!response.ok) {
    return { status: response.status, error: formatted?.error };
  }
  return { status: response.status, data: formatted.user };
}

/**
 * Creates a new user or sends a sign-in link. New emails get an activation
 * email; existing emails get a sign-in magic link instead.
 * @param email The user's email address
 * @returns Response with success or error
 */
export async function signIn(email: string) {
  const response = await fetch(`${API_BASE_URL}/v1/users`, {
    method: 'POST',
    body: JSON.stringify({
      email: email,
    }),
  });
  const formatted = await response.json();
  return formatted;
}

// The support/contact endpoints deliberately skip the X-Frontend-Key header the
// other /v1/frontend/* writes send. Omitting it (and every other non-safelisted
// header — the JSON string body defaults to the safelisted text/plain
// Content-Type) keeps these POSTs CORS-"simple", so they skip the OPTIONS
// preflight entirely. credentials: "include" still ships the gcapi_session
// cookie, which these endpoints require to identify the sender; the backend's
// Origin allowlist plus SameSite=Lax remain the CSRF defense in place of the
// preflight the key would have forced.

/**
 * Sends a support message to the team (delivered to support@golfcourseapi.com
 * server-side). Requires a signed-in session.
 * @param email The sender's email address (for the team to reply to)
 * @param message The support message body
 * @returns The parsed JSON response
 */
export async function sendSupportMessage(email: string, message: string) {
  const response = await fetch(`${API_BASE_URL}/v1/frontend/support`, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify({ email, message }),
  });
  return await response.json();
}

/**
 * Sends a feature request to the team (delivered to support@golfcourseapi.com
 * server-side). Requires a signed-in session.
 * @param email The requester's email address
 * @param message The feature request body
 * @returns The parsed JSON response
 */
export async function sendFeatureRequest(email: string, message: string) {
  const response = await fetch(`${API_BASE_URL}/v1/frontend/contact`, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify({ email, message }),
  });
  return await response.json();
}

/**
 * Activates a user account with a token. The backend also sets the session
 * cookie on success, so the user lands signed in — credentials: "include"
 * is required for the browser to store that cross-origin Set-Cookie.
 * @param token The activation token
 * @returns Response with user data or error
 */
export async function activateAccount(token: string) {
  const response = await fetch(`${API_BASE_URL}/v1/users/activated`, {
    method: 'PUT',
    credentials: 'include',
    body: JSON.stringify({
      token,
    }),
  });
  const formatted = await response.json();
  return formatted;
}

/**
 * Searches for golf courses by name via the frontend-only endpoint. The
 * signed-in user comes from the session cookie (must be pro/enterprise).
 * Results carry only a tee box count per grouping, not the tee boxes — call
 * fetchCourse with the id for the full course.
 * @param query The search query
 * @param signal Aborts the request — used to cancel superseded typeahead calls
 * @returns Matching course summaries with response status
 */
export async function searchCourses(
  query: string,
  signal?: AbortSignal
): Promise<ApiResult<CourseSearchResult[]>> {
  const response = await frontendFetch(
    `/v1/frontend/search?search_query=${encodeURIComponent(
      query
    )}&fuzzy_match=true`,
    { signal }
  );
  const formatted = await response.json();
  if (!response.ok) {
    return { status: response.status, error: formatted?.error };
  }
  return { status: response.status, data: formatted.courses ?? [] };
}

/**
 * Fetches a single golf course by ID (session-authenticated)
 * @param id The course ID
 * @returns The course with response status
 */
export async function fetchCourse(id: number): Promise<ApiResult<GolfCourse>> {
  const response = await frontendFetch(`/v1/frontend/courses/${id}`);
  const formatted = await response.json();
  if (!response.ok) {
    return { status: response.status, error: formatted?.error };
  }
  return { status: response.status, data: formatted.course };
}

/**
 * Partially updates a golf course. Only the fields included in the payload
 * are changed; when `tees` is present it replaces the course's tee boxes
 * wholesale. Requires a pro/enterprise session.
 * @param id The course ID
 * @param payload The fields to update
 * @returns The updated course with response status
 */
export async function updateCourse(
  id: number,
  payload: CourseUpdatePayload
): Promise<ApiResult<GolfCourse>> {
  const response = await frontendFetch(`/v1/frontend/courses/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  const formatted = await response.json();
  if (!response.ok) {
    return { status: response.status, error: formatted?.error };
  }
  return { status: response.status, data: formatted.course };
}
